import {
  useContainerRuntime,
  useSaveContainerRuntime,
} from "@via/pages/account/profile/run-environments/container-runtime.api.tsx";
import { useDataRoots } from "@via/pages/account/profile/run-environments/data-roots.api.tsx";
import { detailOf, statusOf } from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import {
  CONTAINER_RUNTIME_KINDS,
  type ContainerApprovalMode,
  type ContainerNetworkIsolation,
  type ContainerRuntimeKind,
  type RunEnvironmentContainerRuntimeResponseBody,
  type RunEnvironmentDataRoot,
  type RunEnvironmentTransport,
  type RunEnvironmentWorkspaceCaps,
  type SaveRunEnvironmentContainerRuntimeRequestBody,
} from "common";
import {
  type JSX,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

export const READY_LINE = "Ready for workspace jobs";
export const BORROWED_LINE =
  "Files written through a read and write folder belong to the owner's cluster account.";
export const NOT_EDITOR_LINE =
  "Only the run environment's owner, an admin of its organization or a platform admin can manage its container execution.";
export const PARTIAL_CAPS_LINE =
  "Fill in all five caps, or leave all five empty.";
export const SUMMARY_LOADING = "Loading";
export const SUMMARY_NOT_EDITOR = "Owner and admins only";
export const SUMMARY_UNREADABLE = "Could not be loaded";
export const PREFILLED_LINE =
  "Suggested values are filled in where nothing is saved yet. Nothing is saved until you select Save container execution.";

/**
 * The site's suggested modules and image (CONTAINER_RUNTIME_DEFAULT_MODULES and
 * CONTAINER_RUNTIME_DEFAULT_IMAGE), offered only while the environment has no saved runtime.
 */
export interface ContainerSiteDefaults {
  modules: string[];
  image: string | null;
}

/** The caps a card with none saved starts from. Storage stays empty: no storage limit. */
const DEFAULT_WORKSPACE_CAPS: RunEnvironmentWorkspaceCaps = {
  maxLiveWorkspaces: 5,
  maxLiveJobs: 10,
  maxWindowCpuMinutes: 240,
  windowHours: 4,
  maxJobWallMinutes: 1000,
  maxStorageMb: null,
};

/** A runtime's binary is named after it, so that is the Command a new kind starts from. */
const defaultCommandFor = (kind: ContainerRuntimeKind): string => kind;

const NETWORK_TEXT: Readonly<Record<ContainerNetworkIsolation, string>> = {
  enforced: "Enforced",
  unavailable: "Not available",
  unmeasured: "Not measured yet",
};

/**
 * Whether the run environment form shows this card. It ships dark: only while the installation
 * turned AGENT_WORKSPACE_JOBS_ENABLED on, and on both transports, because workspace jobs run over
 * the agent and over SSH alike.
 */
export const containerExecutionShown = (
  workspaceJobsEnabled: boolean | undefined,
  transport: RunEnvironmentTransport | undefined
): boolean =>
  Boolean(workspaceJobsEnabled) &&
  ("agent" === transport || "ssh" === transport);

/**
 * What still keeps workspace jobs from running here, one line per missing piece, worked out from
 * what is stored (never from unsaved edits). Empty means ready.
 */
export const workspaceReadiness = (
  data: RunEnvironmentContainerRuntimeResponseBody,
  roots: RunEnvironmentDataRoot[]
): string[] => {
  const missing: string[] = [];
  if (!data.runtime) missing.push("No container runtime");
  if (roots.length === 0) missing.push("No data folders");
  if (!data.workspaceCaps) missing.push("No workspace caps");
  const isolated = data.runtime?.networkIsolation === "enforced";
  if (!isolated && !data.runtime?.allowWithoutNetworkIsolation) {
    missing.push(
      "Network isolation is not enforced, and jobs may not run without it"
    );
  }
  return missing;
};

/**
 * The collapsed card's one line. While the stored settings are still arriving, or when the GET is
 * refused (a borrower, or anyone else who is not an editor), it says so in neutral words rather
 * than claiming the card is Off.
 */
export const containerExecutionCardSummary = (
  data: RunEnvironmentContainerRuntimeResponseBody | undefined,
  roots: RunEnvironmentDataRoot[] | undefined,
  state: { loading?: boolean; error?: unknown } = {}
): string => {
  if (state.error) {
    return statusOf(state.error) === 403
      ? SUMMARY_NOT_EDITOR
      : SUMMARY_UNREADABLE;
  }
  if (state.loading) return SUMMARY_LOADING;
  if (!data || !roots) return "Off";
  const missing = workspaceReadiness(data, roots).length;
  if (missing === 0) return "Ready";
  return 1 === missing ? "1 item missing" : `${missing} items missing`;
};

interface ContainerForm {
  kind: ContainerRuntimeKind;
  command: string;
  modules: string;
  image: string;
  approvalMode: ContainerApprovalMode;
  allowWithoutNetworkIsolation: boolean;
  maxLiveWorkspaces: string;
  maxLiveJobs: string;
  maxWindowCpuMinutes: string;
  windowHours: string;
  maxJobWallMinutes: string;
  maxStorageMb: string;
  maxChainDepth: string;
  maxWaitingSteps: string;
}

/** The three optional overrides sit outside the all or none five: each is null when left blank. */
type OptionalCapKey = "maxStorageMb" | "maxChainDepth" | "maxWaitingSteps";
type CapKey = Exclude<keyof RunEnvironmentWorkspaceCaps, OptionalCapKey>;

const CAP_FIELDS: readonly {
  key: CapKey;
  label: string;
  hint?: string;
  max?: number;
}[] = [
  { key: "maxLiveWorkspaces", label: "Live workspaces per user" },
  {
    key: "maxLiveJobs",
    label: "Live jobs per user",
    hint: "Across all of one person's workspaces on this environment.",
  },
  { key: "maxWindowCpuMinutes", label: "CPU minutes per window" },
  {
    key: "windowHours",
    label: "Window (hours)",
    hint: "At most 168.",
    max: 168,
  },
  { key: "maxJobWallMinutes", label: "Longest job (minutes)" },
];

export const STORAGE_LIMIT_LABEL = "Storage per user (MB)";
export const STORAGE_LIMIT_HINT =
  "Optional. Empty means no storage limit. New jobs and workspaces are refused once a person's workspaces hold this much. A job that is already running can still write past it.";
export const CHAIN_DEPTH_LABEL = "Longest chain of steps";
export const CHAIN_DEPTH_HINT =
  "Optional, 1 to 10. Empty means 10. How many dependent steps one chain may hold.";
export const WAITING_STEPS_LABEL = "Waiting steps per workspace";
export const WAITING_STEPS_HINT =
  "Optional, 1 to 200. Empty means 20. Steps one workspace may hold while they wait for an earlier job.";
export const CHAIN_NEEDS_CAPS_LINE =
  "The chain limits are saved with the other five caps. Fill in all five, or clear the chain limits.";
export const STORAGE_NEEDS_CAPS_LINE =
  "The storage limit is saved with the other five caps. Fill in all five, or clear the storage limit.";

/**
 * The form as stored. With `prefill`, a part that has nothing saved (the runtime, or the caps) starts
 * from the suggested values instead of empty fields; a saved part is always shown as saved.
 */
const formFrom = (
  data: RunEnvironmentContainerRuntimeResponseBody,
  prefill?: ContainerSiteDefaults
): ContainerForm => {
  const runtime = data.runtime;
  const caps = data.workspaceCaps ?? (prefill ? DEFAULT_WORKSPACE_CAPS : null);
  const siteRuntime = !runtime && prefill ? prefill : undefined;
  const text = (value: number | undefined): string =>
    value === undefined ? "" : String(value);
  return {
    kind: runtime?.kind ?? "apptainer",
    command: runtime?.command ?? "apptainer",
    modules: (runtime?.modules ?? siteRuntime?.modules ?? []).join(", "),
    image: runtime?.image ?? siteRuntime?.image ?? "",
    approvalMode: runtime?.approvalMode ?? "foundry",
    allowWithoutNetworkIsolation:
      runtime?.allowWithoutNetworkIsolation ?? false,
    maxLiveWorkspaces: text(caps?.maxLiveWorkspaces),
    maxLiveJobs: text(caps?.maxLiveJobs),
    maxWindowCpuMinutes: text(caps?.maxWindowCpuMinutes),
    windowHours: text(caps?.windowHours),
    maxJobWallMinutes: text(caps?.maxJobWallMinutes),
    maxStorageMb: text(caps?.maxStorageMb ?? undefined),
    maxChainDepth: text(caps?.maxChainDepth ?? undefined),
    maxWaitingSteps: text(caps?.maxWaitingSteps ?? undefined),
  };
};

/** All five caps, none of them, or null when only some are filled in. */
const capsFrom = (
  form: ContainerForm
): RunEnvironmentWorkspaceCaps | undefined | null => {
  const filled = CAP_FIELDS.filter(({ key }) => form[key].trim() !== "");
  if (filled.length === 0) return undefined;
  if (filled.length !== CAP_FIELDS.length) return null;
  return {
    maxLiveWorkspaces: Number(form.maxLiveWorkspaces),
    maxLiveJobs: Number(form.maxLiveJobs),
    maxWindowCpuMinutes: Number(form.maxWindowCpuMinutes),
    windowHours: Number(form.windowHours),
    maxJobWallMinutes: Number(form.maxJobWallMinutes),
    // Always sent: an absent field clears the stored limit on the server, the chain caps included.
    maxStorageMb:
      form.maxStorageMb.trim() === "" ? null : Number(form.maxStorageMb),
    maxChainDepth:
      form.maxChainDepth.trim() === "" ? null : Number(form.maxChainDepth),
    maxWaitingSteps:
      form.maxWaitingSteps.trim() === "" ? null : Number(form.maxWaitingSteps),
  };
};

const swallowEnter = (event: KeyboardEvent<HTMLInputElement>): void => {
  if (event.key === "Enter") event.preventDefault();
};

const inputClass =
  "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";
const labelClass = "block text-sm font-medium leading-6 text-gray-900";
const primaryButton =
  "rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * How workspace jobs run on a run environment (agent-7-4, #1040): the container runtime, who
 * approves each job, whether jobs may run where network isolation is not available, and the per
 * user workspace caps. A save sends the runtime declaration and the caps, and leaves the apps blocks
 * out (agent-d78): the Apps card owns them and an absent block keeps what is stored, so a copy read
 * before an Apps card save in another tab can never be written back over it. Nothing measured is
 * ever declared.
 * Every range rule is the server's: a refusal is shown in the API's own words.
 *
 * Like the Apps card, nothing here is registered with the form: Save is its own PUT and every
 * button is type="button".
 */
export function ContainerExecutionCard({
  environmentId,
  borrowed = false,
  siteDefaults = { modules: [], image: null },
}: {
  environmentId?: number;
  borrowed?: boolean;
  siteDefaults?: ContainerSiteDefaults;
}): JSX.Element {
  const runtime = useContainerRuntime(environmentId);
  const dataRoots = useDataRoots(environmentId);
  const save = useSaveContainerRuntime(environmentId);
  const [form, setForm] = useState<ContainerForm | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Seeded once, from the first read. The query refetches on its own (every 5 seconds while an
  // apps check is pending, and whenever the Apps card saves), and reseeding on each of those would
  // silently throw away what the person is typing. A successful save reseeds from the fresh read.
  const seeded = useRef(false);
  useEffect(() => {
    if (runtime.data && !seeded.current) {
      seeded.current = true;
      setForm(formFrom(runtime.data, siteDefaults));
      setPrefilled(!runtime.data.runtime || !runtime.data.workspaceCaps);
    }
  }, [runtime.data, siteDefaults]);

  // A borrower is refused the runtime GET (only the owner and admins may read it), so the ownership
  // sentence goes ahead of every early return: they see it next to the not editor line.
  const borrowedNote = borrowed ? (
    <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
      {BORROWED_LINE}
    </p>
  ) : null;
  const framed = (content: JSX.Element): JSX.Element =>
    borrowedNote ? (
      <div className="space-y-5">
        {borrowedNote}
        {content}
      </div>
    ) : (
      content
    );

  if (!environmentId) {
    return (
      <p className="text-sm text-gray-600">
        Save this run environment first, then set up container execution on it.
      </p>
    );
  }
  if (runtime.isLoading) {
    return framed(
      <p className="text-sm text-gray-600">Loading container execution</p>
    );
  }
  if (runtime.isError) {
    return framed(
      <p className="text-sm text-gray-600">
        {statusOf(runtime.error) === 403
          ? NOT_EDITOR_LINE
          : "The container execution settings could not be loaded."}
      </p>
    );
  }
  const data = runtime.data;
  if (!data || !form) {
    return framed(
      <p className="text-sm text-gray-600">Loading container execution</p>
    );
  }

  const missing = dataRoots.data
    ? workspaceReadiness(data, dataRoots.data)
    : null;
  const networkIsolation: ContainerNetworkIsolation =
    data.runtime?.networkIsolation ?? "unmeasured";

  const edit = (patch: Partial<ContainerForm>): void => {
    setForm(previous => (previous ? { ...previous, ...patch } : previous));
    setSaveError(null);
    setSavedNotice(false);
  };

  const submit = async (): Promise<void> => {
    setSaveError(null);
    setSavedNotice(false);
    const workspaceCaps = capsFrom(form);
    if (workspaceCaps === null) {
      setSaveError(PARTIAL_CAPS_LINE);
      return;
    }
    if (workspaceCaps === undefined && form.maxStorageMb.trim() !== "") {
      setSaveError(STORAGE_NEEDS_CAPS_LINE);
      return;
    }
    if (
      workspaceCaps === undefined &&
      (form.maxChainDepth.trim() !== "" || form.maxWaitingSteps.trim() !== "")
    ) {
      setSaveError(CHAIN_NEEDS_CAPS_LINE);
      return;
    }
    const body: SaveRunEnvironmentContainerRuntimeRequestBody = {
      kind: form.kind,
      command: form.command.trim(),
      modules: form.modules
        .split(",")
        .map(module => module.trim())
        .filter(module => module !== ""),
      image: form.image.trim() === "" ? null : form.image.trim(),
      approvalMode: form.approvalMode,
      allowWithoutNetworkIsolation: form.allowWithoutNetworkIsolation,
      // Running on the agent host is not offered yet (agent-7-3b): keep what is stored.
      localExecutionAllowed: data.runtime?.localExecutionAllowed ?? false,
    };
    if (workspaceCaps) body.workspaceCaps = workspaceCaps;
    try {
      await save.mutateAsync(body);
    } catch (failure) {
      setSaveError(
        detailOf(failure) ??
          "The container execution settings could not be saved."
      );
      return;
    }
    setSavedNotice(true);
    setPrefilled(false);
    const fresh = await runtime.refetch();
    if (fresh.data) setForm(formFrom(fresh.data));
  };

  return (
    <div className="space-y-5">
      {borrowedNote}
      {prefilled ? (
        <p className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
          {PREFILLED_LINE}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="container-kind" className={labelClass}>
            Runtime
          </label>
          <select
            id="container-kind"
            className={`mt-1 ${inputClass}`}
            value={form.kind}
            onChange={event => {
              const kind = event.target.value as ContainerRuntimeKind;
              // Command follows the runtime only while it still holds the old runtime's default,
              // so a path someone typed is never overwritten.
              const followsDefault =
                form.command.trim() === "" ||
                form.command.trim() === defaultCommandFor(form.kind);
              edit(
                followsDefault
                  ? { kind, command: defaultCommandFor(kind) }
                  : { kind }
              );
            }}>
            {CONTAINER_RUNTIME_KINDS.map(kind => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="container-command" className={labelClass}>
            Command
          </label>
          <input
            id="container-command"
            type="text"
            className={`mt-1 font-mono ${inputClass}`}
            value={form.command}
            onKeyDown={swallowEnter}
            onChange={event => edit({ command: event.target.value })}
          />
        </div>
        <div>
          <label htmlFor="container-modules" className={labelClass}>
            Modules
          </label>
          <input
            id="container-modules"
            type="text"
            className={`mt-1 font-mono ${inputClass}`}
            aria-describedby="container-modules-hint"
            value={form.modules}
            onKeyDown={swallowEnter}
            onChange={event => edit({ modules: event.target.value })}
          />
          <p id="container-modules-hint" className="mt-1 text-xs text-gray-500">
            Comma separated, loaded in order before the command.
          </p>
        </div>
        <div>
          <label htmlFor="container-image" className={labelClass}>
            Image
          </label>
          <input
            id="container-image"
            type="text"
            className={`mt-1 font-mono ${inputClass}`}
            aria-describedby="container-image-hint"
            value={form.image}
            onKeyDown={swallowEnter}
            onChange={event => edit({ image: event.target.value })}
          />
          <p id="container-image-hint" className="mt-1 text-xs text-gray-500">
            The one image jobs run in. Leave it empty to use the workspace
            image.
          </p>
        </div>
      </div>

      <fieldset>
        <legend className={labelClass}>Approval</legend>
        <div className="mt-2 space-y-2">
          {(
            [
              ["foundry", "Ask me before each job runs"],
              ["client", "My AI client asks me"],
            ] as const
          ).map(([mode, label]) => (
            <div key={mode} className="flex items-center gap-2">
              <input
                id={`container-approval-${mode}`}
                type="radio"
                name="container-approval"
                className="h-4 w-4 border-gray-300 text-indigo-600"
                checked={form.approvalMode === mode}
                onChange={() => edit({ approvalMode: mode })}
              />
              <label
                htmlFor={`container-approval-${mode}`}
                className="text-sm text-gray-900">
                {label}
              </label>
            </div>
          ))}
        </div>
      </fieldset>

      <div className="space-y-2">
        <p id="container-network-state" className="text-sm text-gray-900">
          Network isolation:{" "}
          <span className="font-medium">{NETWORK_TEXT[networkIsolation]}</span>
        </p>
        <div className="flex items-center gap-2">
          <input
            id="container-allow-network"
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            aria-describedby="container-network-state container-network-hint"
            checked={form.allowWithoutNetworkIsolation}
            onChange={event =>
              edit({ allowWithoutNetworkIsolation: event.target.checked })
            }
          />
          <label
            htmlFor="container-allow-network"
            className="text-sm text-gray-900">
            Run jobs even when network isolation is not available
          </label>
        </div>
        <p id="container-network-hint" className="text-xs text-gray-600">
          Jobs could reach the network.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {CAP_FIELDS.map(({ key, label, hint, max }) => (
          <div key={key}>
            <label htmlFor={`container-cap-${key}`} className={labelClass}>
              {label}
            </label>
            <input
              id={`container-cap-${key}`}
              type="number"
              min={1}
              max={max}
              aria-describedby={hint ? `container-cap-${key}-hint` : undefined}
              className={`mt-1 ${inputClass}`}
              value={form[key]}
              onKeyDown={swallowEnter}
              onChange={event => edit({ [key]: event.target.value })}
            />
            {hint ? (
              <p
                id={`container-cap-${key}-hint`}
                className="mt-1 text-xs text-gray-500">
                {hint}
              </p>
            ) : null}
          </div>
        ))}
        <div>
          <label htmlFor="container-cap-maxStorageMb" className={labelClass}>
            {STORAGE_LIMIT_LABEL}
          </label>
          <input
            id="container-cap-maxStorageMb"
            type="number"
            min={1}
            max={1073741824}
            aria-describedby="container-cap-maxStorageMb-hint"
            className={`mt-1 ${inputClass}`}
            value={form.maxStorageMb}
            onKeyDown={swallowEnter}
            onChange={event => edit({ maxStorageMb: event.target.value })}
          />
          <p
            id="container-cap-maxStorageMb-hint"
            className="mt-1 text-xs text-gray-500">
            {STORAGE_LIMIT_HINT}
          </p>
        </div>
        <div>
          <label htmlFor="container-cap-maxChainDepth" className={labelClass}>
            {CHAIN_DEPTH_LABEL}
          </label>
          <input
            id="container-cap-maxChainDepth"
            type="number"
            min={1}
            max={10}
            aria-describedby="container-cap-maxChainDepth-hint"
            className={`mt-1 ${inputClass}`}
            value={form.maxChainDepth}
            onKeyDown={swallowEnter}
            onChange={event => edit({ maxChainDepth: event.target.value })}
          />
          <p
            id="container-cap-maxChainDepth-hint"
            className="mt-1 text-xs text-gray-500">
            {CHAIN_DEPTH_HINT}
          </p>
        </div>
        <div>
          <label htmlFor="container-cap-maxWaitingSteps" className={labelClass}>
            {WAITING_STEPS_LABEL}
          </label>
          <input
            id="container-cap-maxWaitingSteps"
            type="number"
            min={1}
            max={200}
            aria-describedby="container-cap-maxWaitingSteps-hint"
            className={`mt-1 ${inputClass}`}
            value={form.maxWaitingSteps}
            onKeyDown={swallowEnter}
            onChange={event => edit({ maxWaitingSteps: event.target.value })}
          />
          <p
            id="container-cap-maxWaitingSteps-hint"
            className="mt-1 text-xs text-gray-500">
            {WAITING_STEPS_HINT}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          className={primaryButton}
          disabled={save.isPending}
          onClick={submit}>
          Save container execution
        </button>
        {/* Always mounted, so a screen reader hears the notice when it appears. */}
        <span role="status" className="text-sm text-gray-600">
          {savedNotice ? "Container execution saved." : null}
        </span>
      </div>
      {saveError ? (
        <p className="text-sm text-red-600" role="alert">
          {saveError}
        </p>
      ) : null}

      <div
        data-testid="container-execution-readiness"
        role="status"
        className="space-y-1 border-t border-gray-100 pt-4 text-sm">
        {null === missing ? (
          <p className="text-gray-600">
            {dataRoots.isError
              ? "The data folders could not be read."
              : "Checking readiness"}
          </p>
        ) : missing.length === 0 ? (
          <p className="text-green-700">{READY_LINE}</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            {missing.map(line => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
